<script setup lang="ts">
import { Vue3Lottie } from 'vue3-lottie'
import EggJson from '@/partners/lingo/lingo-giveaway/assets/lotties/egg.json'
import { computed, nextTick, ref, watch } from 'vue'
import CrackedEggIcon from '@/partners/lingo/lingo-giveaway/components/icons/CrackedEggIcon.vue'
import LingoGiveawayEggCards from '@/partners/lingo/lingo-giveaway/components/LingoGiveawayEggCards.vue'
import LingoGiveawayConnectWalletModal
  from '@/partners/lingo/lingo-giveaway/components/modals/LingoGiveawayTransactionModal.vue'
import LingoGiveawayCardOverlay from '@/partners/lingo/lingo-giveaway/components/overlays/LingoGiveawayCardOverlay.vue'
import type { EggGameCardCharacter } from '@/partners/lingo/lingo-giveaway/types/egg-game'
import { gameCharacters, goldCharacterId } from '@/partners/lingo/lingo-giveaway/config/characters'
import LingoGiveawayEggGameMintSection
  from '@/partners/lingo/lingo-giveaway/components/LingoGiveawayEggGameMintSection.vue'
import { useLingoGiveaway } from '@/partners/lingo/lingo-giveaway/composables/lingo-giveaway'
import { type GACHA_PRIZE } from '@/partners/lingo/lingo-giveaway/types/shared/gacha'
import LingoGiveawayTransactionOverlay
  from '@/partners/lingo/lingo-giveaway/components/overlays/LingoGiveawayTransactionOverlay.vue'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import { useConfetti } from '@/composables/game/confetti'
import { useLingoGameSounds } from '@/partners/lingo/lingo-giveaway/composables/sounds'
import EggNotReadyModal from '@/partners/lingo/lingo-giveaway/components/modals/EggNotReadyModal.vue'
import CrackNewEggModal from '@/partners/lingo/lingo-giveaway/components/modals/CrackNewEggModal.vue'

const props = defineProps<{
  checkGlowSound: number
}>()

const animationSegments = {
  0: 'purple_idle',
  180: 'purple_ready',
  240: 'purple_ready-idle',
  300: 'purple_crack1',
  360: 'purple_crack2',
  420: 'purple_crack3',
  480: 'purple_open',
  540: 'purple_reset',
  600: 'gold_idle',
  780: 'gold_ready',
  840: 'gold_ready-idle',
  900: 'gold_crack1',
  960: 'gold_crack2',
  1020: 'gold_crack3',
  1080: 'gold_open',
  1140: 'gold_reset',
}
const currentSegment = ref(animationSegments[0])

const {
  countDownSeconds,
  isCracking,
  crack,
  addPrize,
  pendingMint,
  resetPendingMint,
  isGold,
  setGold,
  setEggWasClicked,
  hasExtraHatch,
  resetExtraHatch,
} = useLingoGiveaway()
const { addHapticFeedback } = useHapticFeedback()
const { showConfetti } = useConfetti()
const { playEggAngelicSound, playEggCrackSound, playUiClaimSound, playUiClickDisabledSound } = useLingoGameSounds()

const crackStates = [animationSegments[240], animationSegments[300], animationSegments[360], animationSegments[420], animationSegments[480], animationSegments[840], animationSegments[900], animationSegments[960], animationSegments[1020], animationSegments[1080]]

const eggLottie = ref<typeof Vue3Lottie | null>(null)
const loop = ref(true)
const eggInitialised = ref(false)
const cracks = ref(0)
const disabledCracks = ref(0)

const showConnectWalletModal = ref(false)
const showEggNotReadyModal = ref(false)
const showCrackNewEggModal = ref(false)
const showCardOverlay = ref(false)
const showTransactionOverlay = ref(false)
const mintTicketEntries = computed(() => pendingMint.value.count)
const isInMintableState = ref(false)
const showMintTicket = computed(() => mintTicketEntries.value > 0 && isInMintableState.value === true)
const activePrize = ref<EggGameCardCharacter | null>(null)
const activeCharacter = ref<EggGameCardCharacter | null>(null)
const isMinting = ref(false)
const isTransactionLoading = ref<boolean>(false)
const isTransactionFailed = ref<boolean>(false)

const isInInitState = computed(() => !isGold.value && currentSegment.value === animationSegments[180] || isGold.value && currentSegment.value === animationSegments[780])
const isInGlowState = computed(() => !isGold.value && currentSegment.value === animationSegments[240] || isGold.value && currentSegment.value === animationSegments[840])
const isInCrackState = computed(() => cracks.value > 0)
const floatingBoxContent = computed(() => {
  if (isInGlowState.value) return 'Tap the egg to crack'
  if (cracks.value === 1) return 'Keep cracking'
  if (cracks.value === 2) return 'A little harder'
  if (cracks.value === 3) return 'Almost there'
  return 'Revealing...'
})

function setSegment(segments: number[]) {
  eggLottie.value?.playSegments(segments, true)
  currentSegment.value = animationSegments[segments[segments.length - 1] + 1 as keyof typeof animationSegments]
}

function initEggAnimation() {
  if (eggInitialised.value) {
    glowEgg()
    return
  }
  nextTick(() => {
    if (eggLottie.value) {
      setSegment([0, 179])
      eggInitialised.value = true
      setTimeout(() => {
        if (isInInitState.value) {
          glowEgg()
        }
      }, 3000)
    } else {
      console.error('eggLottie is not initialized.')
    }
  })
}

function glowEgg() {
  if (!isGold.value && countDownSeconds.value > 0) {
    return
  }
  playEggAngelicSound()
  loop.value = false
  setSegment(!isGold.value ? [180, 239] : [780, 839])
}

function crackEgg() {
  if (cracks.value < 3) {
    addHapticFeedback()
  }
  switch (cracks.value) {
  case 0:
    playEggCrackSound(0)
    setEggWasClicked(true)
    setSegment(!isGold.value ? [300, 359] : [900, 959])
    cracks.value++
    break
  case 1:
    playEggCrackSound(1)
    setSegment(!isGold.value ? [360, 419] : [960, 1019])
    cracks.value++
    break
  case 2:
    playEggCrackSound(2)
    setSegment(!isGold.value ? [420, 479] : [1020, 1079])
    cracks.value++
    break
  case 3:
    if (isGold.value) {
      cracks.value++
      playEggCrackSound(3)
      setTimeout(() => playUiClaimSound(), 300)
      setActiveCard(goldCharacterId)
    } else {
      showConnectWalletModal.value = true
    }
  }
}

function handleEggClick() {
  if (countDownSeconds.value > 0) {
    playUiClickDisabledSound()
    disabledCracks.value++
    if (disabledCracks.value % 2 === 0) {
      setTimeout(() => showEggNotReadyModal.value = true, 200)
    }
    return
  }
  if (crackStates.includes(currentSegment.value)) {
    crackEgg()
  }
}

async function doCrack() {
  cracks.value++
  setTimeout(() => isTransactionLoading.value = true, 500)

  const prize = await crack()
  if (prize === undefined) {
    isTransactionLoading.value = false
    isTransactionFailed.value = true
    cracks.value--
  } else if (prize) {
    isTransactionLoading.value = false
    showConnectWalletModal.value = false
    playEggCrackSound(3)
    playUiClaimSound()
    setTimeout(() => addHapticFeedback(), 300)
    setActiveCard(prize)
  } else {
    isTransactionLoading.value = false
    cracks.value--
  }
}

function onTransactionModalClose() {
  isTransactionFailed.value = false
  if (!isTransactionLoading.value) {
    showConnectWalletModal.value = false
  }
}

function setActiveCard(prize: GACHA_PRIZE | string) {
  setSegment(!isGold.value ? [480, 539] : [1080, 1139])
  activePrize.value = gameCharacters.find(character => character.id === prize) ?? null
  setTimeout(() => {
    showCardOverlay.value = true
    setTimeout(() => {
      if (eggLottie.value)
        eggLottie.value.stop()
      setTimeout(() => {
        showConfetti({ spread: 120, colors: isGold.value ? ['#fbe83d', '#d9a701', '#be801a'] : undefined })
      }, 500)
    }, 100)
  }, 300)
  setTimeout(() => {
    cracks.value = 0
  }, 400)
}

function incrementPrize() {
  if (!activePrize.value) return

  addPrize(activePrize.value.id as GACHA_PRIZE)
}

function restartEggAnimation() {
  if (isGold.value || countDownSeconds.value <= 0) {
    glowEgg()
    return
  }
  setSegment([0, 179])
}

async function onMintClick() {
  if (eggLottie.value)
    eggLottie.value.pause()
  isMinting.value = true
  try {
    playUiClaimSound()
    showTransactionOverlay.value = true
    setTimeout(() => {
      showConfetti({ spread: 120 })
    }, 500)
  } finally {
    isMinting.value = false
    setTimeout(() => isInMintableState.value = false, 200)
  }
}

function onCardOverlayClick() {
  if (activeCharacter.value) {
    showCardOverlay.value = false
    setTimeout(() => activeCharacter.value = null, 250)
    return
  }
  setSegment(!isGold.value ? [530, 539] : [1130, 1139])

  if (isGold.value) {
    setGold(false)
    showCardOverlay.value = false
    restartEggAnimation()
    return
  }

  incrementPrize()
  showCardOverlay.value = false
  setTimeout(() => isInMintableState.value = true, 100)
  onMintClick()
}

function resetGame(checkForExtraHatch = false) {
  setTimeout(() => {
    isInMintableState.value = false
    resetPendingMint()
    restartEggAnimation()
    activePrize.value = null
    if (checkForExtraHatch && hasExtraHatch.value) {
      setTimeout(() => {
        showCrackNewEggModal.value = true
        resetExtraHatch()
      }, 700)
    }
  }, 200)
}

function showCardForCharacter(characterId: GACHA_PRIZE | typeof goldCharacterId) {
  activeCharacter.value = gameCharacters.find(character => character.id === characterId) ?? null
  setTimeout(() => showCardOverlay.value = true, 50)
}

watch(countDownSeconds, (countDownSeconds, oldValue) => {
  if (countDownSeconds <= 0 && oldValue > 0 && isInInitState.value) glowEgg()
})

watch(() => props.checkGlowSound, () => {
  if (!isInGlowState.value) return
  playEggAngelicSound()
})
</script>

<template>
  <div
    class="w-full"
  >
    <div
      class="w-full h-[380px] min-[430px]:h-[432px] rounded-xl game-bg bg-cover min-[430px]:bg-cover bg-no-repeat relative scale-in"
      :class="{'pointer-events-none': isCracking}"
    >
      <div class="w-full h-full bg-gray-gradient">
        <div
          v-if="isInInitState"
          class="w-full pt-8 px-6 flex flex-col gap-y-2 text-white"
        >
          <div class="text-[20px] leading-6 font-semibold uppercase text-shadow-title text-center">
            CRACK THE EGG AND WIN DAILY
          </div>
          <div class="text-sm leading-[22px] font-semibold -tracking-[0.16px] text-shadow-title text-center">
            Collect Mavericks for a chance to win prizes in daily prize
            draws, and guaranteed reward.
          </div>
        </div>
        <div
          v-else-if="isInGlowState || isInCrackState"
          class="w-fit mx-auto pt-8 float"
        >
          <div
            class="bg-white rounded-xl px-6 py-4 flex items-center gap-x-2 text-black"
            :class="{'shake': cracks > 3}"
          >
            <div
              class="text-lg text-shadow font-bold tracking-[1px] uppercase"
            >
              {{ floatingBoxContent }}
            </div>
            <CrackedEggIcon class="w-5 h-8 min-w-[20px]" />
          </div>
        </div>
        <div
          class="w-[80%] max-w-[360px] absolute bottom-[50px] min-[370px]:bottom-[30px] left-1/2 -translate-x-1/2"
        >
          <Transition
            name="fade"
          >
            <div
              v-if="eggInitialised && !showMintTicket"
              class="w-1/2 h-[55%] absolute left-1/2 -translate-x-1/2 top-[45px] z-[10] cursor-pointer"
              @click="handleEggClick"
            />
            <LingoGiveawayEggGameMintSection
              v-else-if="showMintTicket && activePrize"
              :entries="mintTicketEntries"
              :prize-image="activePrize.image"
              class="absolute left-1/2 -translate-x-1/2 top-1/2 translate-y-[calc(-50%-36px)] z-[10]"
              :loading="isMinting"
              @hide="resetGame"
            />
          </Transition>
          <Vue3Lottie
            ref="eggLottie"
            :animation-data="EggJson"
            :auto-play="false"
            :loop="loop"
            @on-animation-loaded="initEggAnimation"
          />
        </div>
        <LingoGiveawayEggCards
          class="absolute -bottom-8"
          @show-card="showCardForCharacter"
        />
      </div>
    </div>

    <LingoGiveawayConnectWalletModal
      :show="showConnectWalletModal"
      :is-loading="isTransactionLoading"
      :is-failed="isTransactionFailed"
      @hide="onTransactionModalClose"
      @complete="doCrack"
      @on-connected="isTransactionFailed = false"
    />

    <EggNotReadyModal
      :show="showEggNotReadyModal"
      :count-down-seconds="countDownSeconds"
      @hide="showEggNotReadyModal = false"
    />

    <CrackNewEggModal
      :show="showCrackNewEggModal"
      @hide="showCrackNewEggModal = false"
    />

    <LingoGiveawayCardOverlay
      v-if="activePrize || activeCharacter"
      :show="showCardOverlay"
      :content="(activePrize ?? activeCharacter) as (NonNullable<typeof activePrize> | NonNullable<typeof activeCharacter>)"
      @cta-click="onCardOverlayClick"
    />

    <LingoGiveawayTransactionOverlay
      v-if="mintTicketEntries > 0"
      :show="showTransactionOverlay"
      :entries="mintTicketEntries"
      @click="showTransactionOverlay = false; resetGame(true)"
    />
  </div>
</template>

<style scoped lang="scss">
.game-bg {
  background-image: url('@/partners/lingo/lingo-giveaway/assets/images/game-bg.png');
}

.text-shadow {
  text-shadow: 0 0 30px #000;
}

.text-shadow-title {
  text-shadow: 0 0 12px #000;
}

.bg-gray-gradient {
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.00) 87.18%, #000 100%);
}
</style>
